
import { Component, forwardRef, KeyValueDiffers, Inject, OnChanges, ViewChild, OnInit, ElementRef, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { map } from 'rxjs/operators';

import * as htmlToImage from 'html-to-image';
import * as papaparse from 'papaparse';
import * as am4core from '@amcharts/amcharts4/core';

import { BlockComponent } from '../../../PageBuilder/components/Block/block.component';
import { SeriesModel } from '../../../Charts/models/series.model';
import { AbstractPageBuilderService } from '../../../PageBuilder/services/abstract-page-builder.service';
import { ChartDisplayElementModel } from '../../models/chart-display-element.model';
import { ReportBlockTypeEnum } from '../../enums/report-block-type.enum';
import { ReportsPageBuilderService } from '../../services/reports-page-builder.service';
import { ChartExportEventModel } from '../../../Charts/models/chart-export-event.model';
import { ChartBlockComponent } from '../ChartBlock/chart-block.component';
import { ReportsSeriesPreviewService } from '../../services/report-series-preview.service';
import { ReportsChartOptionsService } from '../../services/reports-chart-options.service';
import { DataListChartComponent } from '../../../Charts/components/DataListChart/data-list-chart.component';
import { DataListChartOptionsModel } from '../../../Charts/models/data-list-chart-options.model';
import { printHelper } from '../../../Misc/helpers/print/print.helper';
import { download } from '../../../Misc/helpers/download/download.helper';
import { chartColors } from '../../../Charts/enums/chart-colors.enum';
import { CoverageSummaryChartDisplayElementModel } from '../../models/coverage-summary-chart-display-element.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { MonitorConfigService } from '../../../MonitorShared/services/monitor-config.service';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { ClickOutsideService } from '../../../../Common/PageBuilder/services/click-outside.service';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { ReportsChartDetailService } from '../../services/reports-chart-detail.service';
import { ChartMetricsEnum } from '../../enums/chart-metrics.enum';
import { SocialChartDetailsDialogService } from '../../../../Social/Intelligence/services/social-chart-details-dialog.service';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { ChartSizeHelper } from '@Common/Charts/helpers/chart-size.helper';
import { DataConnectionService } from '../../../../Core/Users/services/data-connection.service';
import { ReportsChartSeriesConverterService } from '../../services/reports-chart-series-converter.service';
import { ReportsSeriesPreviewNewService } from '../../../../Common/Reports/services/reports-series-preview-new.service';

@Component({
    selector: 'agpr-coverage-summary-chart-block',
    templateUrl: './coverage-summary-chart-block.component.html',
    styleUrls: ['./coverage-summary-chart-block.component.scss'],
    providers: [
        {
            provide: BlockComponent,
            useExisting: forwardRef(() => CoverageSummaryChartBlockComponent)
        }
    ],
    standalone: false
})
export class CoverageSummaryChartBlockComponent extends ChartBlockComponent implements OnChanges, OnInit, OnDestroy {
    protected exportClass: string = 'agpr-coverageSummaryChart--exporting';
    declare chartOptions: DataListChartOptionsModel;
    series: SeriesModel[];
    sourceRouterLink: string = '';
    sourceRouterLinkQueryParams: any = {};
    sourceId: string = '';
    isExporting: boolean = false;

    noHeader: boolean;
    hasPreviousPeriod: boolean;
    wide: boolean = false;
    tall: boolean = false;
    skinny: boolean = false;
    chartResizeObserver: ResizeObserver = new ResizeObserver(entries => {
        const width = entries[0].contentRect.width;
        const height = entries[0].contentRect.height;
        // the skinny mode is for when the width is too small to show label, value and previous period in one line
        this.skinny = width < 240;
        this.wide = width > ChartSizeHelper.getMinWidthAtMinCanvas();
        this.tall = height > ChartSizeHelper.getCellHeight();
        this.changeDetectorRef.detectChanges();
    });

    @ViewChild('dataListChart') dataListChart: DataListChartComponent;
    @ViewChild('exportMenu') exportMenu;

    constructor(
        public differs: KeyValueDiffers,
        public pageBuilderService: AbstractPageBuilderService,
        public reportSeriesPreviewService: ReportsSeriesPreviewService,
        public reportsChartOptionsService: ReportsChartOptionsService,
        public elementRef: ElementRef,
        public localizationService: LocalizationService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public changeDetectorRef: ChangeDetectorRef,
        public userStateService: CurrentUserStateService,
        public monitorConfigService: MonitorConfigService,
        protected userPreferencesService: UserPreferencesService,
        public clickOutsideService: ClickOutsideService,
        public reportsChartDetailService: ReportsChartDetailService,
        public socialChartDetailsDialogService: SocialChartDetailsDialogService,
        public configService: ConfigService,
        public dataConnectionService: DataConnectionService,
        public reportsChartSeriesConverterService: ReportsChartSeriesConverterService,
        public reportsSeriesPreviewNewService: ReportsSeriesPreviewNewService
    ) {
        super(
            differs,
            pageBuilderService,
            reportSeriesPreviewService,
            reportsChartOptionsService,
            localizationService,
            appBaseHref,
            changeDetectorRef,
            userStateService,
            null,
            monitorConfigService,
            userPreferencesService,
            clickOutsideService,
            reportsChartDetailService,
            socialChartDetailsDialogService,
            configService,
            elementRef,
            dataConnectionService,
            reportsChartSeriesConverterService,
            reportsSeriesPreviewNewService
        );
    }

    ngOnInit(): void {
        this.chartOptions = this.reportsChartOptionsService.getDataListChartOptions();
        super.ngOnInit();
        this.initSourceRouterLink();
        this.chartResizeObserver.observe(this.elementRef.nativeElement.children[0]);
    }

    ngOnDestroy(): void {
        this.chartResizeObserver.unobserve(this.elementRef.nativeElement.children[0]);
        this.chartResizeObserver.disconnect();
    }

    ngOnChanges(): void {
        this.renderable = this.block.displayElement.canRender();
        this.updateChart();
        this.noHeader = !this.displayElement.chartHeaderSettings?.title &&
        !this.displayElement.chartHeaderSettings?.dateRange && (this.displayElement.chartHeaderSettings?.multiSource ? true :
            !this.displayElement.chartHeaderSettings?.sourceLink);
        this.hasPreviousPeriod = this.displayElement.chartHeaderSettings?.previousPeriod;
        this.changeDetectorRef.detectChanges();
    }

    updateChart() {
        this.renderable = this.block.displayElement.canRender();

        this.displayElement = this.block.displayElement as ChartDisplayElementModel;
        if (!this.displayElement || !this.displayElement.series || this.displayElement.series.length < 1) {
            this.series = null;
            return;
        }

        let series = this.displayElement.series.filter(seriesItem => seriesItem.isValid());
        if (series.length < 1) {
            this.series = null;
            return;
        }

        series.forEach(_series => _series.hasPreviousPeriod = this.displayElement.styles.chartDisplaySettings.previousPeriod);

        if (this.preview) {
            series = this.reportSeriesPreviewService.getPreviewSeries(this.block.type.key as ReportBlockTypeEnum, series);
        }

        this.series = series.map(originalSeries => {
            const newSeries: SeriesModel = originalSeries.clone();

            const removeInvalidMetrics: Function = (value) => {
                const selectedOption: string[] = this.displayElement.metrics ?? [
                    ChartMetricsEnum.volumeOfMentions,
                    ChartMetricsEnum.audienceReach,
                    ChartMetricsEnum.ave
                ];

                if (value?.length && selectedOption?.length) {
                    value = value
                        .filter(x => selectedOption.indexOf(x.id) !== -1)
                        .sort((a, b) => selectedOption.indexOf(a.id) - selectedOption.indexOf(b.id));
                }

                return value;
            };

            newSeries.dataProvider = originalSeries.dataProvider.pipe(map((value) => removeInvalidMetrics(value)));
            newSeries.previousPeriodDataProvider = originalSeries.previousPeriodDataProvider.pipe(map((value) => removeInvalidMetrics(value)));

            return newSeries;
        });

        this.setLabels();
        (this.pageBuilderService as ReportsPageBuilderService).mapBlockToChartComponent(this.block, this);
    }

    protected setLabels(): void  {
        if (this.displayElement?.series?.[0]?.dateRange
            // For shared report, we already set its relative and absolute date rage when mapping api blocks
            // For date range "ALL" scenario, we cannot do localization in model, so we keep it here
            && (!this.displayElement.series[0].isSharedReport || this.displayElement?.series?.[0]?.dateRange.type === DateQueryTypeEnum.ALL)
        ) {
            this.displayElement.dateRangeLabel = this.reportSeriesPreviewService.dateRangeToString(this.displayElement.series);
        }
    }

    /**
     * Gets the series label from the chart
     */
    public getExportSeriesLabel(): string {
        if (this.series && this.series[0]) {
            return this.series[0].label || '';
        } else {
            return '';
        }
    }

    /**
     * Handles the export button click action, exporting the chart in the selected format
     */
    public async chartExport(event: ChartExportEventModel) {
        if (!this.dataListChart.chartContainer) {
            return;
        }
        this.isExporting = true;

        // this removes the :hover rule that causes the border of the chart to be blue
        const container: HTMLDivElement = this.elementRef.nativeElement.children[0];
        container.classList.remove('agpr-block');

        this.exportMenu.nativeElement.style.display = 'none';

        const exportType = event.eventKey.toLowerCase() as keyof am4core.IExportOptions;
        let url: string;
        let blob: Blob;

        switch (exportType) {
            case 'jpg':
                url = await htmlToImage.toJpeg(this.elementRef.nativeElement.children[0]);
                break;

            case 'png':
                url = await htmlToImage.toPng(this.elementRef.nativeElement.children[0]);
                break;

            case 'svg':
                url = await htmlToImage.toSvg(this.elementRef.nativeElement.children[0]);
                break;

            case 'csv':
                const csvLabels = [];
                const csvValues = [];
                const csvData = [
                    csvLabels,
                    csvValues
                ];

                this.dataListChart.data.forEach((item) => {
                    const label = item.csvLabel || item.label;
                    const previousPeriodLabel = (item.csvLabel || item.label) + ' - ' + this.chartOptions.previousPeriodLabel;

                    let value = '' + item.xValue;
                    let previousPeriodValue = '' + item.xValuePreviousPeriod;


                    if (item.currency) {
                        value = item.xValue.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2});
                        if (this.hasPreviousPeriod) {
                            previousPeriodValue = item.xValuePreviousPeriod.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2});
                        }
                    } else if (item.numberFormat) {
                        value = this.dataListChart.decimalPipe.transform(value, <string>item.numberFormat);
                        if (this.hasPreviousPeriod) {
                            previousPeriodValue = this.dataListChart.decimalPipe.transform(previousPeriodValue, <string>item.numberFormat);
                        }

                    } else if (item.dateFormat) {
                        value = this.dataListChart.datePipe.transform(value, <string>item.dateFormat);
                        if (this.hasPreviousPeriod) {
                            previousPeriodValue = this.dataListChart.datePipe.transform(previousPeriodValue, <string>item.dateFormat);
                        }
                    }

                    csvLabels.push(label);
                    csvValues.push(value);

                    if (this.hasPreviousPeriod) {
                        csvLabels.push(previousPeriodLabel);
                        csvValues.push(previousPeriodValue);
                    }
                });

                const csvString: string = papaparse.unparse(csvData, {delimiter: ','});

                blob = new Blob(['\ufeff', csvString], { type: 'text/csv;charset=utf-8;'});
                break;

            case 'json':
                const jsonData = {};
                this.dataListChart.data.forEach((item) => {
                    const previousPeriodJsonLabel = (item.jsonLabel || item.label) + ' - ' + this.chartOptions.previousPeriodLabel;

                    if (item.currency) {
                        jsonData[(item.jsonLabel || item.label) as string] = this.dataListChart.currencyPipe.transform(item.xValue);
                        if (this.hasPreviousPeriod) {
                            jsonData[previousPeriodJsonLabel] = this.dataListChart.currencyPipe.transform(item.xValuePreviousPeriod as number);
                        }
                    }else{
                        jsonData[(item.jsonLabel || item.label) as string] = item.xValue;
                        if (this.hasPreviousPeriod) {
                            jsonData[previousPeriodJsonLabel] = item.xValuePreviousPeriod;
                        }
                    }
                });

                blob = new Blob([JSON.stringify(jsonData)], { type: 'text/json;charset=utf-8;'});
                break;

            case 'print':
                const printElement = this.elementRef.nativeElement.children[0];
                const printData = await htmlToImage.toSvg(printElement);
                printHelper.image(printData, document.title, {fit: printElement.offsetHeight <= 1500});
                break;
        }

        if (blob) {
            download.blob(blob, `${this.displayElement.title}.${exportType}`);

        } else if (url) {
            download.url(url, `${this.displayElement.title}.${exportType}`);
        }

        this.exportMenu.nativeElement.style.display = 'block';
        container.classList.add('agpr-block');
        // Always reset isExporting to false to close spinner
        this.isExporting = false;
    }

    /**
     * Gets the export image for the full report pdf export
     */
    public async getFullExportImage(width: number, height: number): Promise<string> {
        if (!this?.dataListChart?.data) {
            return null;
        }

        const el: HTMLDivElement = this.elementRef.nativeElement.children[0];
        const classes: DOMTokenList = this.elementRef.nativeElement.children[0].classList;

        el.style.border = 'none';

        // Sets a bunch of styles for scaling to the page size of the pdf
        classes.add(this.exportClass);
        el.style.width = width + 'px';
        el.style.minWidth = width + 'px';
        el.style.height = height + 'px';
        el.style.minHeight = height + 'px';
        el.style.flex = 'unset';

        // Gets the image data URI
        const png = await htmlToImage.toPng(this.elementRef.nativeElement.children[0]);

        // Resets the styles to the original
        el.style.width = '';
        el.style.minWidth = '';
        el.style.height = '';
        el.style.minHeight = '';
        el.style.flex = '';

        el.style.border = `1px solid ${chartColors.agprLightGrey}`;

        classes.remove(this.exportClass);

        return png;
    }

    /**
     * Constructs the url to the topic or folder currently being used as a source for this chart.
     * Tags & Tag Groups are also handled, by constructing a url to all saved coverage with appropriate filters
     * applied via queryParams
     */
    public initSourceRouterLink(): void {
        const displayElement = this.block.displayElement as CoverageSummaryChartDisplayElementModel;

        if (displayElement?.series?.[0]) {
            const urlParts = displayElement.series[0].getMentionSeriesUrl().split('?filters=');
            this.sourceRouterLink = '/' + urlParts?.[0];
            this.sourceRouterLinkQueryParams = { filters: urlParts?.[1] };
        }
    }

    formatDateRangeLabel(format: string) {
        this.displayElement.dateRangeLabel = this.reportSeriesPreviewService.dateRangeToString(this.displayElement.series, null, format);
        this.changeDetectorRef.detectChanges();
    }
}
